import {
    getFallbackAccountRoleForLineItem,
    LineItemFallbackAccountType,
} from "@common/helpers/accounting";
import { Taxonomy } from "@common/categories";
import { Account, AccountRole } from "@common/types/domains/accounting/account";
import { EntityProfileType } from "@common/types/entity";
import { getAccountCodeForTaxonomy } from "@common/constants/domains/accounting/taxonomyMapping";
import { TransactionMatchSchema } from "@common/schemas/transactionMatch.schema";

type MatchedDocument = NonNullable<
    TransactionMatchSchema["invoice"] | TransactionMatchSchema["bill"]
>;

interface GetDefaultApArAccountIdParams {
    transaction: AccrualAccountTransaction;
    accounts: Account[];
}

export function getMatchedDocument(
    match: TransactionMatchSchema,
): MatchedDocument | null {
    return match.invoice ?? match.bill ?? null;
}

export function getFirstMatchedDocument(
    transactionMatches?: TransactionMatchSchema[],
): MatchedDocument | null {
    if (!transactionMatches) {
        return null;
    }
    for (const match of transactionMatches) {
        const doc = getMatchedDocument(match);
        if (doc) {
            return doc;
        }
    }
    return null;
}

interface AccrualAccountTransaction {
    amount: number;
    transactionMatches?: TransactionMatchSchema[];
    category?: { taxonomy?: Taxonomy | null } | null;
    entity: { legalType: EntityProfileType | null };
    deductionRate?: number | null;
}

interface GetDefaultAccrualAccountIdBaseParams {
    transaction: AccrualAccountTransaction;
    accounts: Account[];
    lineItemAccountId?: string | null;
}

function getAccountIdFromMatch(
    transactionMatches?: TransactionMatchSchema[],
): string | null {
    if (!transactionMatches) {
        return null;
    }

    for (const match of transactionMatches) {
        const source = getMatchedDocument(match);
        if (source?.lineItemAccountId) {
            return source.lineItemAccountId;
        }
    }

    return null;
}

export function findAccountByOriginalCode(
    accounts: Account[],
    code: number,
): Account | undefined {
    return accounts.find((a) => a.kickOriginalCode === code && !a.isDisabled);
}

export function findAccountByRole(
    accounts: Account[],
    role: AccountRole,
): Account | undefined {
    return accounts.find((account) => account.role === role);
}

export function findEnabledAccountByRole(
    accounts: Account[],
    role: AccountRole,
): Account | undefined {
    return accounts.find(
        (account) => account.role === role && !account.isDisabled,
    );
}

interface ResolveAccrualLineItemDefaultAccountIdsParams {
    mode: "invoices" | "bills";
    accounts: Account[];
}

interface AccrualLineItemDefaultAccountIds {
    accountId: string | null;
    accrualAccountId: string | null;
}

export function resolveAccrualLineItemDefaultAccountIds({
    mode,
    accounts,
}: ResolveAccrualLineItemDefaultAccountIdsParams): AccrualLineItemDefaultAccountIds {
    // Empty accounts means the ledger hasn't loaded yet; nothing to resolve.
    if (accounts.length === 0) {
        return { accountId: null, accrualAccountId: null };
    }

    const incomeOrExpenseType =
        mode === "invoices"
            ? LineItemFallbackAccountType.invoiceRevenue
            : LineItemFallbackAccountType.billExpense;
    const apArType =
        mode === "invoices"
            ? LineItemFallbackAccountType.invoiceAccountsReceivable
            : LineItemFallbackAccountType.billAccountsPayable;

    const resolveByType = (
        lineItemFallbackAccountType: LineItemFallbackAccountType,
    ): string | null => {
        const role = getFallbackAccountRoleForLineItem({
            lineItemFallbackAccountType,
        });
        return findEnabledAccountByRole(accounts, role)?.id ?? null;
    };

    return {
        accountId: resolveByType(incomeOrExpenseType),
        accrualAccountId: resolveByType(apArType),
    };
}

function getAccountIdFromTaxonomy(
    transaction: AccrualAccountTransaction,
    accounts: Account[],
): string | null {
    const taxonomy = transaction.category?.taxonomy;
    if (!taxonomy) {
        return null;
    }

    const code = getAccountCodeForTaxonomy({
        taxonomy,
        isIncomingTransaction: transaction.amount > 0,
        entityType: transaction.entity.legalType,
        deductionRate: transaction.deductionRate ?? undefined,
    });

    if (code === null) {
        return null;
    }

    return findAccountByOriginalCode(accounts, code)?.id ?? null;
}

export function getDefaultAccrualAccountId({
    transaction,
    accounts,
    lineItemAccountId,
    ignoreMatches,
    hasDocumentMatch: hasDocumentMatchOverride,
}: GetDefaultAccrualAccountIdBaseParams & {
    ignoreMatches?: boolean;
    hasDocumentMatch?: boolean;
}): string | null {
    // Empty accounts means the ledger hasn't loaded yet; nothing to resolve.
    if (accounts.length === 0) {
        return null;
    }

    if (!ignoreMatches) {
        const matchAccountId =
            lineItemAccountId ??
            getAccountIdFromMatch(transaction.transactionMatches);
        if (
            matchAccountId &&
            accounts.some((a) => a.id === matchAccountId && !a.isDisabled)
        ) {
            return matchAccountId;
        }

        const hasDocumentMatch =
            hasDocumentMatchOverride ??
            transaction.transactionMatches?.some((m) => getMatchedDocument(m));

        if (hasDocumentMatch) {
            return getDefaultApArAccountId({
                transaction,
                accounts,
            });
        }
    }

    const taxonomyAccountId = getAccountIdFromTaxonomy(transaction, accounts);
    if (taxonomyAccountId) {
        return taxonomyAccountId;
    }

    const isMoneyIn = transaction.amount > 0;
    const fallbackMode = isMoneyIn
        ? LineItemFallbackAccountType.invoiceRevenue
        : LineItemFallbackAccountType.billExpense;

    const fallbackRole = getFallbackAccountRoleForLineItem({
        lineItemFallbackAccountType: fallbackMode,
    });

    return findEnabledAccountByRole(accounts, fallbackRole)?.id ?? null;
}

function getDefaultApArAccountId({
    transaction,
    accounts,
}: GetDefaultApArAccountIdParams): string | null {
    const isMoneyIn = transaction.amount > 0;
    const fallbackType = isMoneyIn
        ? LineItemFallbackAccountType.invoiceAccountsReceivable
        : LineItemFallbackAccountType.billAccountsPayable;

    const role = getFallbackAccountRoleForLineItem({
        lineItemFallbackAccountType: fallbackType,
    });

    return findEnabledAccountByRole(accounts, role)?.id ?? null;
}
